import { parsePresetPixelSize } from "../../../../../utils/getTransform";

/** Per-side guardrails for custom resize (px). */
export const CUSTOM_SIZE_MIN = 100;
export const CUSTOM_SIZE_MAX = 4000;
/** Long-edge cap for "Max size" suggestion (same as per-side max). */
export const CUSTOM_SIZE_MAX_EDGE = 4000;

const RECENT_KEY = "recentPresets";
const RECENT_MAX = 5;

function gcd(a, b) {
  let x = Math.round(Math.abs(a));
  let y = Math.round(Math.abs(b));
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

/** Lowest-terms aspect label, e.g. "3:4". */
export function aspectRatioLabelForSize(width, height) {
  const w = Math.round(Number(width));
  const h = Math.round(Number(height));
  if (!(w > 0 && h > 0)) return "";
  const g = gcd(w, h);
  return `${Math.round(w / g)}:${Math.round(h / g)}`;
}

export function clampCustomSize(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return null;
  return Math.min(CUSTOM_SIZE_MAX, Math.max(CUSTOM_SIZE_MIN, n));
}

export function isCustomResizePreset(preset) {
  if (!preset) return false;
  if (preset.is_custom === true || preset.is_custom === 1 || preset.is_custom === "1") {
    return true;
  }
  const slug = String(preset.slug || "").toLowerCase();
  if (slug === "standard-custom" || slug.endsWith("-custom")) return true;
  const id = String(preset.id || "");
  if (id.startsWith("custom-")) return true;
  const name = String(preset.name || "").trim().toLowerCase();
  return name === "custom";
}

/** Catalog Custom row with no usable pixel size (0×0 / Make your size). */
export function isUnresolvedCustomPreset(preset) {
  if (!isCustomResizePreset(preset)) return false;
  return !parsePresetPixelSize(preset);
}

/** Catalog Standard "Custom" tile — opens the Custom Size panel (not a pixel preset). */
export function isCatalogCustomTile(preset) {
  if (!preset) return false;
  if (isUnresolvedCustomPreset(preset)) return true;
  const slug = String(preset.slug || "").toLowerCase();
  if (slug === "standard-custom") return true;
  // API Custom row is often 0×0 with label Custom in Standard.
  const w = Number(preset.width);
  const h = Number(preset.height);
  const name = String(preset.name || "").trim().toLowerCase();
  if (name === "custom" && (!(w > 0) || !(h > 0))) return true;
  return false;
}

export function buildCustomResizePreset(width, height, base = null) {
  const w = clampCustomSize(width);
  const h = clampCustomSize(height);
  if (w == null || h == null) return null;
  const aspect_ratio = aspectRatioLabelForSize(w, h);
  const dimensions = `${w} × ${h} px`;
  return {
    ...(base && typeof base === "object" ? base : {}),
    id: `custom-${w}x${h}`,
    name: "Custom",
    label: "Custom",
    slug: base?.slug || "standard-custom",
    width: w,
    height: h,
    aspect_ratio,
    dimensions,
    dims_label: dimensions,
    is_custom: true,
  };
}

/**
 * Scale shortcuts from source natural size.
 * Max size: preserve AR, long edge capped at CUSTOM_SIZE_MAX_EDGE.
 */
export function getCustomSizeSuggestions(naturalW, naturalH) {
  const w = Math.round(Number(naturalW));
  const h = Math.round(Number(naturalH));
  if (!(w > 0 && h > 0)) return [];

  const ar = aspectRatioLabelForSize(w, h);
  const origW = clampCustomSize(w) ?? w;
  const origH = clampCustomSize(h) ?? h;
  const halfW = clampCustomSize(Math.round(w / 2)) ?? CUSTOM_SIZE_MIN;
  const halfH = clampCustomSize(Math.round(h / 2)) ?? CUSTOM_SIZE_MIN;
  const doubleW = clampCustomSize(w * 2) ?? CUSTOM_SIZE_MAX;
  const doubleH = clampCustomSize(h * 2) ?? CUSTOM_SIZE_MAX;

  const long = Math.max(w, h);
  const scale = long > 0 ? CUSTOM_SIZE_MAX_EDGE / long : 1;
  const maxW = clampCustomSize(Math.round(w * scale)) ?? CUSTOM_SIZE_MAX;
  const maxH = clampCustomSize(Math.round(h * scale)) ?? CUSTOM_SIZE_MAX;

  const fmt = (rw, rh) => `${rw} × ${rh} px, ${ar}`;

  return [
    { id: "original", label: "Original size", width: origW, height: origH, meta: fmt(origW, origH) },
    { id: "half", label: "Half size", width: halfW, height: halfH, meta: fmt(halfW, halfH) },
    { id: "double", label: "Double size", width: doubleW, height: doubleH, meta: fmt(doubleW, doubleH) },
    { id: "max", label: "Max size", width: maxW, height: maxH, meta: fmt(maxW, maxH) },
  ];
}

function dimensionsKey(preset) {
  const parsed = parsePresetPixelSize(preset);
  if (parsed) return `${parsed.width}×${parsed.height}`;
  return String(preset?.dimensions || preset?.dims_label || "").trim();
}

export function addRecentResizePreset(preset) {
  if (!preset) return;
  const dimensions =
    dimensionsKey(preset) ||
    (preset.width && preset.height ? `${preset.width} × ${preset.height}` : "");
  const toStore = { ...preset, dimensions: dimensions || preset.dimensions };
  try {
    const saved = localStorage.getItem(RECENT_KEY);
    const prev = saved ? JSON.parse(saved) : [];
    const list = Array.isArray(prev) ? prev : [];
    const filtered = list.filter(
      (p) =>
        !(
          p.name === preset.name &&
          dimensionsKey(p) === dimensionsKey(toStore)
        )
    );
    const updated = [toStore, ...filtered].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent("resize-recent-presets-changed"));
  } catch {
    // ignore
  }
}
